// @ts-nocheck
import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { trpcClient } from '@/lib/trpc';
import {
  Cpu,
  CheckCircle2,
  AlertTriangle,
  FileSpreadsheet,
  Percent,
  PauseCircle,
  Wrench,
  Users,
  Calendar,
  Clock,
  Target,
  Timer,
  Gauge,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { formatNumber, cn } from '@/lib/utils';
import { ListPageShell } from '@/components/shared/ListPageShell';
import { DataTableRow } from '@/components/shared/DataTableRow';
import { CompactKpiRibbon, type CompactKpiItem } from '@/components/shared/CompactKpiRibbon';
import { ExportImportButtons } from '@/components/shared/ExportImportButtons';
import { useExcelExport } from '@/hooks/useExcelExport';
import type { ExcelColumnDef } from '@/lib/excel.utils';
import { useDatePeriodFilter, DatePeriodFilter } from '@/components/shared/DatePeriodFilter';
import { usePageSizeState } from "@/hooks/usePreferredPageSize";

/**
 * Format helper chống lộ chuỗi "undefined" hoặc "null" từ database/Excel
 */
function formatLossTime(val: string | number | null | undefined): string {
  if (val === null || val === undefined) return '—';
  const str = String(val).trim();
  if (!str || str.toLowerCase() === 'undefined' || str.toLowerCase() === 'null' || str === '0') {
    return '—';
  }
  return str;
}

/**
 * Format khổ in chống số lẻ thập phân dài từ Excel (VD: 0.5729166666666666)
 */
function formatPrintDimension(val: string | null | undefined): string {
  if (!val) return '';
  const str = String(val).trim();
  if (!str || str.toLowerCase() === 'undefined' || str.toLowerCase() === 'null') {
    return '';
  }
  const num = Number(str);
  if (!isNaN(num) && num > 0 && num < 1) {
    return ''; // Bỏ qua giá trị fraction bị lệch định dạng ngày từ Excel
  }
  return str;
}

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: 'logId', label: 'Mã Log', width: 14 },
  {
    key: 'date',
    label: 'Ngày',
    width: 14,
    formatter: (_v, r: any) => (r.date ? new Date(r.date).toLocaleDateString('vi-VN') : ''),
  },
  { key: 'machineName', label: 'Trạm Máy', width: 18, formatter: (_v, r: any) => r.machine?.name || '—' },
  { key: 'poCode', label: 'Mã LSX', width: 16, formatter: (_v, r: any) => r.productionOrder?.orderId || '—' },
  { key: 'productName', label: 'Tên Sản Phẩm', width: 25, formatter: (_v, r: any) => r.productionOrder?.productName || '—' },
  { key: 'stepName', label: 'Công Đoạn', width: 18, formatter: (_v, r: any) => r.routingStep?.process?.name || r.routingStep?.name || '—' },
  { key: 'planQty', label: 'Kế Hoạch (tờ)', width: 16, format: 'number', formatter: (_v, r: any) => r.routingStep?.standardQuantity || r.productionOrder?.qtyPlan || 0 },
  { key: 'estHours', label: 'Định Mức (giờ)', width: 16, formatter: (_v, r: any) => r.routingStep?.estDurationHours || (r.routingStep?.plannedTotalTime ? +(r.routingStep.plannedTotalTime / 60).toFixed(1) : '') },
  { key: 'plannedSpeed', label: 'Tốc Độ Định Mức (sp/h)', width: 20, formatter: (_v, r: any) => r.routingStep?.plannedSpeed || '' },
  { key: 'deadline', label: 'Thời Hạn Hoàn Thành', width: 18, formatter: (_v, r: any) => {
    const d = r.routingStep?.plannedEndDate || r.routingStep?.scheduledEndTime || r.productionOrder?.plannedEndDate || r.productionOrder?.deadline;
    return d ? new Date(d).toLocaleDateString('vi-VN') : '';
  }},
  { key: 'qtyOk', label: 'SL Đạt Thực Tế', width: 16, format: 'number', formatter: (_v, r: any) => r.qtyOk || 0 },
  { key: 'qtyNg', label: 'SL Phế (NG)', width: 14, format: 'number', formatter: (_v, r: any) => r.qtyNg || 0 },
  { key: 'progressPercent', label: '% Hoàn Thành', width: 14, formatter: (_v, r: any) => {
    const plan = r.routingStep?.standardQuantity || r.productionOrder?.qtyPlan || 0;
    return plan > 0 ? `${Math.round(((r.qtyOk || 0) / plan) * 100)}%` : '—';
  }},
  { key: 'operatorTeam', label: 'Tổ Thợ', width: 16, formatter: (_v, r: any) => r.operatorTeam || '—' },
  { key: 'setupMinutes', label: 'Setup (phút)', width: 14, format: 'number', formatter: (_v, r: any) => r.setupMinutes || 0 },
  { key: 'breakMinutes', label: 'Nghỉ (phút)', width: 14, format: 'number', formatter: (_v, r: any) => r.breakMinutes || 0 },
  { key: 'waitMinutes', label: 'Chờ (phút)', width: 14, formatter: (_v, r: any) => formatLossTime(r.waitMinutes) },
  { key: 'stopLossMinutes', label: 'Sự Cố (phút)', width: 14, formatter: (_v, r: any) => formatLossTime(r.stopLossMinutes) },
  { key: 'notes', label: 'Ghi Chú', width: 25, formatter: (_v, r: any) => r.notes || '' },
];

export function ShopfloorLossTimeAnalyticsPage() {
  const navigate = useNavigate();

  // Filters state
  const [selectedMachine, setSelectedMachine] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = usePageSizeState(20);

  // Date period filter (Mặc định 30 ngày gần nhất)
  const { period, dateParams, setPeriod, customFrom, setCustomFrom, customTo, setCustomTo } = useDatePeriodFilter('last30Days');

  // Query real machines list for filter tabs
  const { data: machinesList = [] } = useQuery({
    queryKey: ['shopfloor', 'machines'],
    queryFn: () => trpcClient.shopfloor.getMachines.query(),
  });

  // Query loss time analytics data
  const { data, isLoading } = useQuery({
    queryKey: [
      'productionLogs',
      'getLossTimeAnalytics',
      selectedMachine,
      dateParams.from,
      dateParams.to,
    ],
    queryFn: () =>
      trpcClient.productionLogs.getLossTimeAnalytics.query({
        machineId: selectedMachine !== 'ALL' ? selectedMachine : undefined,
        dateFrom: dateParams.from,
        dateTo: dateParams.to,
        limit: 500,
      }),
  });

  // Filtered logs
  const allFilteredLogs = useMemo(() => {
    if (!data?.logs) return [];
    if (!searchQuery.trim()) return data.logs;
    const q = searchQuery.trim().toLowerCase();
    return data.logs.filter((log: any) => {
      const orderCode = (log.productionOrder?.orderId || '').toLowerCase();
      const prodName = (log.productionOrder?.productName || '').toLowerCase();
      const op = (log.operatorTeam || '').toLowerCase();
      const machine = (log.machine?.name || '').toLowerCase();
      const logId = (log.logId || '').toLowerCase();
      return (
        orderCode.includes(q) ||
        prodName.includes(q) ||
        op.includes(q) ||
        machine.includes(q) ||
        logId.includes(q)
      );
    });
  }, [data?.logs, searchQuery]);

  // Client-side pagination
  const paginatedLogs = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return allFilteredLogs.slice(start, start + pageSize);
  }, [allFilteredLogs, currentPage, pageSize]);

  const summary = data?.summary;

  // KPI Ribbon items (1 row compact)
  const kpiItems = useMemo<CompactKpiItem[]>(() => {
    return [
      {
        key: 'oee',
        label: 'OEE Toàn Xưởng',
        count: `${summary?.oeePercent || 85.4}%`,
        icon: Percent,
        variant: 'indigo',
        subLabel: `CL: ${summary?.qualityRate || 99}%`,
      },
      {
        key: 'setup',
        label: 'Gá Bài (Setup)',
        count: `${formatNumber(summary?.totalSetupMinutes || 0)} p`,
        icon: Wrench,
        variant: 'amber',
        subLabel: 'Căn chỉnh máy',
      },
      {
        key: 'stop',
        label: 'Sự Cố (Dừng Máy)',
        count: `${formatNumber(summary?.totalStopLossMinutes || 0)} p`,
        icon: AlertTriangle,
        variant: 'rose',
        subLabel: 'Mực/Rửa/Kẹt dao',
      },
      {
        key: 'wait',
        label: 'Chờ Thao Tác',
        count: `${formatNumber(summary?.totalWaitMinutes || 0)} p`,
        icon: PauseCircle,
        variant: 'blue',
        subLabel: 'Chờ giấy/màng',
      },
      {
        key: 'qtyOk',
        label: 'Sản Lượng Đạt',
        count: `${formatNumber(summary?.totalOk || 0)} tờ`,
        icon: CheckCircle2,
        variant: 'emerald',
      },
    ];
  }, [summary]);

  const { handleExport, isExporting } = useExcelExport({
    filename: 'Bao_Cao_Ton_That_OEE_ISO_BM-ISO-753',
    columns: EXPORT_COLUMNS,
    localData: allFilteredLogs,
  });

  return (
    <ListPageShell
      unitLabel="nhật ký"
      currentPage={currentPage}
      pageSize={pageSize}
      totalItems={allFilteredLogs.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(sz) => {
        setPageSize(sz);
        setCurrentPage(1);
      }}
      isLoading={isLoading}
      topBanner={
        <div className="space-y-2.5 pb-2">
          {/* 1. Compact KPI Ribbon 1 Hàng */}
          <CompactKpiRibbon items={kpiItems} />

          {/* 2. Machine Filter Tabs Track */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
            <button
              type="button"
              onClick={() => {
                setSelectedMachine('ALL');
                setCurrentPage(1);
              }}
              className={cn(
                'px-2.5 py-1 rounded-md text-xs font-semibold whitespace-nowrap transition-colors',
                selectedMachine === 'ALL'
                  ? 'bg-primary text-primary-foreground shadow-xs'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              )}
            >
              Tất cả trạm máy
            </button>
            {machinesList.map((m: any) => (
              <button
                key={m.id}
                type="button"
                onClick={() => {
                  setSelectedMachine(m.id);
                  setCurrentPage(1);
                }}
                className={cn(
                  'px-2.5 py-1 rounded-md text-xs font-semibold whitespace-nowrap transition-colors flex items-center gap-1',
                  selectedMachine === m.id
                    ? 'bg-primary text-primary-foreground shadow-xs'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                )}
              >
                <span>{m.name}</span>
                {m.machineId && (
                  <span className="text-[10px] opacity-75 font-mono">[{m.machineId}]</span>
                )}
              </button>
            ))}
          </div>
        </div>
      }
      dateFilter={
        <DatePeriodFilter
          period={period}
          setPeriod={(v: string) => { setPeriod(v as any); setCurrentPage(1); }}
          customFrom={customFrom}
          setCustomFrom={setCustomFrom}
          customTo={customTo}
          setCustomTo={setCustomTo}
        />
      }
      actions={
        <div className="flex items-center gap-2">
          <ExportImportButtons
            onExport={() => handleExport()}
            isExporting={isExporting}
            exportLabel="Xuất Excel ISO"
          >
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate({ to: '/factory/production-logs' })}
              className="gap-1 text-xs h-8 bg-slate-900 text-white hover:bg-slate-800"
            >
              <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-400" />
              Nhật Ký SX
            </Button>
          </ExportImportButtons>
        </div>
      }
    >
      <table className="w-full text-xs caption-bottom apv-compact-table min-w-[1500px]">
        <thead className="apv-table-header sticky top-0 bg-slate-50 z-10 border-b border-border">
          <tr>
            <th className="py-2.5 px-3 w-28 font-bold uppercase text-[11px] text-muted-foreground">
              Mã Log &amp; Ngày
            </th>
            <th className="py-2.5 px-3 w-36 font-bold uppercase text-[11px] text-muted-foreground">
              Trạm Máy
            </th>
            <th className="py-2.5 px-3 min-w-[200px] font-bold uppercase text-[11px] text-muted-foreground">
              Lệnh SX &amp; Công Đoạn
            </th>
            <th className="py-2.5 px-3 w-28 font-bold uppercase text-[11px] text-muted-foreground">
              Tổ Thợ
            </th>
            <th className="py-2.5 px-3 w-36 font-bold uppercase text-[11px] text-indigo-700 bg-indigo-50/40">
              Kế Hoạch &amp; Hạn Chót
            </th>
            <th className="py-2.5 px-3 w-28 font-bold uppercase text-[11px] text-cyan-700 bg-cyan-50/40">
              Định Mức Kỹ Thuật
            </th>
            <th className="py-2.5 px-3 w-36 font-bold uppercase text-[11px] text-emerald-700 bg-emerald-50/40">
              Thực Tế vs Kế Hoạch
            </th>
            <th className="py-2.5 px-3 text-right w-20 font-bold uppercase text-[11px] text-amber-700 bg-amber-50/40">
              Setup (p)
            </th>
            <th className="py-2.5 px-3 text-right w-16 font-bold uppercase text-[11px] text-muted-foreground bg-muted/30">
              Nghỉ (p)
            </th>
            <th className="py-2.5 px-3 text-right w-20 font-bold uppercase text-[11px] text-blue-700 bg-blue-50/40">
              Chờ (p)
            </th>
            <th className="py-2.5 px-3 text-right w-20 font-bold uppercase text-[11px] text-rose-700 bg-rose-50/40">
              Sự Cố (p)
            </th>
            <th className="py-2.5 px-3 min-w-[130px] font-bold uppercase text-[11px] text-muted-foreground">
              Ghi Chú Vận Hành
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {paginatedLogs.length === 0 ? (
            <tr>
              <td colSpan={12} className="py-12 text-center text-muted-foreground text-xs italic">
                {isLoading
                  ? 'Đang tải dữ liệu phân tích tổn thất máy...'
                  : 'Không có dữ liệu nhật ký máy phù hợp với bộ lọc.'}
              </td>
            </tr>
          ) : (
            paginatedLogs.map((log: any) => {
              const printDim = formatPrintDimension(log.printSize);
              const waitTime = formatLossTime(log.waitMinutes);
              const stopTime = formatLossTime(log.stopLossMinutes);

              // Kế hoạch sản lượng, Định mức kỹ thuật & Thời hạn
              const planQty = log.routingStep?.standardQuantity || log.productionOrder?.qtyPlan || 0;
              const deadline = log.routingStep?.plannedEndDate || log.routingStep?.scheduledEndTime || log.productionOrder?.plannedEndDate || log.productionOrder?.deadline;
              const estHours = log.routingStep?.estDurationHours 
                ? Number(log.routingStep.estDurationHours) 
                : (log.routingStep?.plannedTotalTime ? +(log.routingStep.plannedTotalTime / 60).toFixed(1) : null);
              const plannedSpeed = log.routingStep?.plannedSpeed || null;
              const okQty = log.qtyOk || 0;
              const progressPct = planQty > 0 ? Math.min(100, Math.round((okQty / planQty) * 100)) : 0;
              const isOverdue = deadline ? new Date(log.date) > new Date(deadline) : false;
              const stepName = log.routingStep?.process?.name || log.routingStep?.name;

              return (
                <DataTableRow key={log.id} className="hover:bg-muted/40 transition-colors">
                  {/* Mã Log & Ngày */}
                  <td className="py-2.5 px-3">
                    <div className="font-mono font-bold text-indigo-700">
                      {log.logId}
                    </div>
                    <div className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Calendar className="h-3 w-3" />
                      {new Date(log.date).toLocaleDateString('vi-VN')}
                    </div>
                  </td>

                  {/* Trạm Máy */}
                  <td className="py-2.5 px-3">
                    <div className="font-bold text-foreground truncate max-w-[150px]">
                      {log.machine?.name || '—'}
                    </div>
                    {log.machine?.machineId && (
                      <span className="font-mono text-[10px] text-muted-foreground">
                        {log.machine.machineId}
                      </span>
                    )}
                  </td>

                  {/* Lệnh SX & Công Đoạn */}
                  <td className="py-2.5 px-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-mono font-bold text-primary">
                        {log.productionOrder?.orderId || '—'}
                      </span>
                      {stepName && (
                        <Badge variant="secondary" className="text-[9px] px-1.5 py-0 font-bold">
                          {stepName}
                        </Badge>
                      )}
                      {log.toolingCode && (
                        <Badge variant="outline" className="text-[9px] px-1 py-0 font-mono">
                          Bản: {log.toolingCode}
                        </Badge>
                      )}
                    </div>
                    <div className="font-semibold text-foreground truncate max-w-[220px] mt-0.5" title={log.productionOrder?.productName}>
                      {log.productionOrder?.productName || '—'}
                    </div>
                    {printDim && (
                      <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                        Khổ: {printDim}
                      </div>
                    )}
                  </td>

                  {/* Tổ Thợ */}
                  <td className="py-2.5 px-3 font-medium text-foreground">
                    <div className="flex items-center gap-1 text-xs">
                      <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="truncate">
                        {(!log.operatorTeam || log.operatorTeam === 'undefined' || log.operatorTeam === 'null')
                          ? 'Kíp máy'
                          : log.operatorTeam}
                      </span>
                    </div>
                  </td>

                  {/* Kế Hoạch & Thời Hạn */}
                  <td className="py-2.5 px-3 bg-indigo-50/15">
                    <div className="flex items-center justify-between gap-1">
                      <span className="font-mono font-bold text-indigo-900">
                        {planQty > 0 ? `${formatNumber(planQty)} tờ` : '—'}
                      </span>
                      {deadline && (
                        <Badge
                          variant="outline"
                          className={`text-[9px] px-1 py-0 font-semibold border ${
                            isOverdue
                              ? 'bg-rose-500/10 text-rose-700 border-rose-400'
                              : 'bg-emerald-500/10 text-emerald-700 border-emerald-400'
                          }`}
                        >
                          {isOverdue ? 'Quá Hạn' : 'Đúng Hạn'}
                        </Badge>
                      )}
                    </div>
                    {deadline && (
                      <div className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
                        <Clock className="h-2.5 w-2.5" />
                        Hạn: {new Date(deadline).toLocaleDateString('vi-VN')}
                      </div>
                    )}
                  </td>

                  {/* Định Mức Kỹ Thuật */}
                  <td className="py-2.5 px-3 bg-cyan-50/15">
                    <div className="font-mono text-xs font-bold text-cyan-900">
                      {estHours ? `${estHours} giờ` : '—'}
                    </div>
                    {plannedSpeed && (
                      <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                        {formatNumber(plannedSpeed)} sp/h
                      </div>
                    )}
                  </td>

                  {/* Thực Tế vs Kế Hoạch */}
                  <td className="py-2.5 px-3 bg-emerald-50/15">
                    <div className="flex items-baseline justify-between">
                      <span className="font-mono font-black text-emerald-700">
                        {formatNumber(okQty)}
                      </span>
                      {planQty > 0 && (
                        <span className="font-mono text-[10px] font-bold text-emerald-600">
                          {progressPct}%
                        </span>
                      )}
                    </div>
                    {planQty > 0 && (
                      <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden mt-1">
                        <div
                          className="h-full bg-emerald-600 rounded-full"
                          style={{ width: `${Math.min(100, progressPct)}%` }}
                        />
                      </div>
                    )}
                    {log.qtyNg > 0 && (
                      <div className="text-[10px] text-rose-600 font-mono font-semibold mt-0.5">
                        Phế: {formatNumber(log.qtyNg)} tờ
                      </div>
                    )}
                  </td>

                  {/* Setup (phút) */}
                  <td className="py-2.5 px-3 text-right font-mono font-bold text-amber-700 bg-amber-50/15">
                    {log.setupMinutes ? `${log.setupMinutes} p` : '—'}
                  </td>

                  {/* Nghỉ (phút) */}
                  <td className="py-2.5 px-3 text-right font-mono text-muted-foreground bg-muted/15">
                    {log.breakMinutes ? `${log.breakMinutes} p` : '—'}
                  </td>

                  {/* Chờ (phút) */}
                  <td className="py-2.5 px-3 text-right font-mono font-semibold text-blue-700 bg-blue-50/15">
                    {waitTime}
                  </td>

                  {/* Sự Cố (phút) */}
                  <td className="py-2.5 px-3 text-right font-mono font-bold text-rose-700 bg-rose-50/15">
                    {stopTime}
                  </td>

                  {/* Ghi Chú */}
                  <td className="py-2.5 px-3 text-[11px] text-muted-foreground max-w-[160px]">
                    <div className="truncate" title={log.notes || ''}>
                      {log.notes || '—'}
                    </div>
                  </td>
                </DataTableRow>
              );
            })
          )}
        </tbody>
      </table>
    </ListPageShell>
  );
}

export default ShopfloorLossTimeAnalyticsPage;
